import { useState } from 'react'
import {
  Badge, Box, Drawer, IconButton, Stack, Tooltip, Typography,
} from '@mui/material'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined'
import ClearAllOutlinedIcon from '@mui/icons-material/ClearAllOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import type { Notification } from '@cmms/api-client'

import { api } from '../lib/api'
import { useConfirm } from './ConfirmDialog'
import { useOrgSettings } from '../hooks/useOrgSettings'

// Each notification type maps to: an icon, an accent color, and a module
// label so the user can see at a glance which module the notification
// belongs to (e.g. "Work order", "Inventory"). Color is used sparingly —
// only on the icon tile — to keep the list calm.
const TYPE_META: Record<string, { icon: React.ElementType; color: string; module: string }> = {
  wo_assigned:      { icon: BuildOutlinedIcon,             color: '#3B82F6', module: 'Work order' },
  wo_completed:     { icon: CheckCircleOutlineIcon,        color: '#059669', module: 'Work order' },
  wo_overdue:       { icon: WarningAmberOutlinedIcon,      color: '#DC2626', module: 'Work order' },
  comment:          { icon: ChatBubbleOutlineOutlinedIcon, color: '#6366F1', module: 'Comment' },
  sla_breach:       { icon: TimerOutlinedIcon,             color: '#DC2626', module: 'SLA' },
  sla_warning:      { icon: TimerOutlinedIcon,             color: '#D97706', module: 'SLA' },
  automation:       { icon: BoltOutlinedIcon,              color: '#7C3AED', module: 'Workflow' },
  low_stock:        { icon: InventoryOutlinedIcon,         color: '#EA580C', module: 'Inventory' },
  request_approved: { icon: CheckCircleOutlineIcon,        color: '#059669', module: 'Request' },
  request_declined: { icon: CancelOutlinedIcon,            color: '#DC2626', module: 'Request' },
  approval_needed:  { icon: AssignmentOutlinedIcon,        color: '#D97706', module: 'Approval' },
  new_member:       { icon: PersonOutlineIcon,             color: '#0D9488', module: 'Team' },
  asset_status:     { icon: WarningAmberOutlinedIcon,      color: '#EA580C', module: 'Asset' },
}

function typeMeta(type: string) {
  return TYPE_META[type] ?? { icon: NotificationsOutlinedIcon, color: '#64748B', module: 'Notification' }
}

function timeAgo(date: string, formatDateShort: (d: Date | string) => string): string {
  const ms = Date.now() - new Date(date).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d`
  return formatDateShort(date)
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)

  const countQ = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => api.notifications.countUnread(),
    refetchInterval: 30_000,
  })
  const count = countQ.data?.count ?? 0

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton size="small" onClick={() => setOpen(true)}
          sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
          <Badge badgeContent={count} color="error" max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, borderRadius: 99 } }}>
            <NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <NotificationDrawer open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function NotificationDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const confirmDlg = useConfirm()

  const notifsQ = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications.list({ limit: 50 }),
    enabled: open,
  })
  const notifications: Notification[] = Array.isArray(notifsQ.data) ? notifsQ.data : []

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] })
    qc.invalidateQueries({ queryKey: ['notification-count'] })
  }

  const markReadM    = useMutation({ mutationFn: (id: string) => api.notifications.markRead(id), onSuccess: invalidate })
  const markAllReadM = useMutation({ mutationFn: () => api.notifications.markAllRead(),          onSuccess: invalidate })
  const removeM      = useMutation({ mutationFn: (id: string) => api.notifications.remove(id),   onSuccess: invalidate })
  const clearAllM    = useMutation({ mutationFn: () => api.notifications.clearAll(),             onSuccess: invalidate })

  function handleClick(n: Notification) {
    if (!n.is_read) markReadM.mutate(n.id)
    if (n.resource_type === 'work_order' && n.resource_id) {
      navigate(`/work-orders/${n.resource_id}`)
      onClose()
    } else if (n.resource_type === 'request' && n.resource_id) {
      navigate('/requests')
      onClose()
    }
  }

  async function handleClearAll() {
    const ok = await confirmDlg({
      title: 'Clear all notifications?',
      message: 'This permanently removes every notification in your inbox. You can\'t undo this.',
      confirmLabel: 'Clear all',
      variant: 'danger',
    })
    if (ok) clearAllM.mutate()
  }

  const unreadCount = notifications.filter((n) => !n.is_read).length

  // Group by date for a calmer list — Today / Yesterday / Earlier.
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)

  const groups: Array<{ label: string; items: Notification[] }> = []
  const todayItems: Notification[] = []
  const yesterdayItems: Notification[] = []
  const olderItems: Notification[] = []

  for (const n of notifications) {
    const d = new Date(n.created_at); d.setHours(0, 0, 0, 0)
    if (d.getTime() === today.getTime()) todayItems.push(n)
    else if (d.getTime() === yesterday.getTime()) yesterdayItems.push(n)
    else olderItems.push(n)
  }

  if (todayItems.length > 0)     groups.push({ label: 'Today',     items: todayItems })
  if (yesterdayItems.length > 0) groups.push({ label: 'Yesterday', items: yesterdayItems })
  if (olderItems.length > 0)     groups.push({ label: 'Earlier',   items: olderItems })

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, bgcolor: 'background.default' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header — flat, no gradient. Single subtle hairline. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5, py: 2,
            borderBottom: '1px solid', borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Notifications
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
              {unreadCount > 0 ? `${unreadCount} unread` : 'You\'re all caught up'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read" arrow>
                <IconButton
                  size="small"
                  onClick={() => markAllReadM.mutate()}
                  disabled={markAllReadM.isPending}
                  sx={{ color: 'text.secondary' }}
                >
                  <DoneAllOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {notifications.length > 0 && (
              <Tooltip title="Clear all" arrow>
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  disabled={clearAllM.isPending}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <ClearAllOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>

        {/* List */}
        <Box sx={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'thin', px: 2, py: 1.5 }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 12, textAlign: 'center' }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '14px',
                bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 1.5,
              }}>
                <NotificationsOutlinedIcon sx={{ fontSize: 26, color: 'primary.main' }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
                Nothing here yet
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
                We'll let you know when something happens.
              </Typography>
            </Box>
          ) : (
            groups.map((group) => (
              <Box key={group.label} sx={{ mb: 2 }}>
                <Typography sx={{
                  fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.10em', color: 'text.disabled',
                  px: 0.5, mb: 0.75,
                }}>
                  {group.label}
                </Typography>
                <Stack spacing={0.75}>
                  {group.items.map((n) => (
                    <NotificationCard
                      key={n.id}
                      notification={n}
                      onClick={() => handleClick(n)}
                      onMarkRead={() => markReadM.mutate(n.id)}
                      onRemove={() => removeM.mutate(n.id)}
                    />
                  ))}
                </Stack>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

function NotificationCard({
  notification: n, onClick, onMarkRead, onRemove,
}: {
  notification: Notification
  onClick: () => void
  onMarkRead: () => void
  onRemove: () => void
}) {
  const { formatDateShort } = useOrgSettings()
  const meta = typeMeta(n.type)
  const Icon = meta.icon

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        px: 1.5, py: 1.25,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid', borderColor: 'divider',
        transition: 'background-color 120ms, border-color 120ms',
        '&:hover': {
          borderColor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.18)',
          bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.02)',
        },
        '&:hover .notif-actions': { opacity: 1 },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        {/* Module icon tile — the only spot of color */}
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          bgcolor: `${meta.color}1A`,
          color: meta.color,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 17 }} />
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
            <Typography sx={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: meta.color,
            }}>
              {meta.module}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', ml: 'auto !important' }}>
              {timeAgo(n.created_at, formatDateShort)}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: 13, fontWeight: n.is_read ? 500 : 700,
              lineHeight: 1.4, color: 'text.primary',
            }}
            noWrap
          >
            {n.title}
          </Typography>
          {n.message && (
            <Typography
              sx={{
                fontSize: 12, color: 'text.secondary', lineHeight: 1.45, mt: 0.25,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}
            >
              {n.message}
            </Typography>
          )}
        </Box>

        {/* Hover actions */}
        <Stack
          className="notif-actions"
          direction="row"
          spacing={0.25}
          sx={{ opacity: 0, transition: 'opacity 120ms', flexShrink: 0, mt: 0.25 }}
        >
          {!n.is_read && (
            <Tooltip title="Mark as read" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onMarkRead() }}
                sx={{ color: 'text.disabled', p: 0.5, '&:hover': { color: 'primary.main' } }}
              >
                <DoneAllOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remove" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRemove() }}
              sx={{ color: 'text.disabled', p: 0.5, '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Tiny unread dot at top-right when not opened yet */}
      {!n.is_read && (
        <Box sx={{
          position: 'absolute', top: 10, right: 10,
          width: 6, height: 6, borderRadius: 99,
          bgcolor: meta.color,
        }} />
      )}
    </Box>
  )
}
